import React from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { DEFAULT_BRANDING_TYPOGRAPHY } from "@root/constants/branding";

interface TypographyTabProps {
  config: IBrandingConfig | null;
  onSave: (typography: IBrandingTypography) => Promise<void>;
  saving: boolean;
}

const FONT_OPTIONS = [
  "'Inter', sans-serif",
  "'Roboto', sans-serif",
  "'Open Sans', sans-serif",
  "'Noto Sans', sans-serif",
  "'Noto Sans KR', sans-serif",
  "'Lato', sans-serif",
  "'Montserrat', sans-serif",
  "'Poppins', sans-serif",
  "'Raleway', sans-serif",
  "'Source Sans Pro', sans-serif",
  "'Playfair Display', serif",
  "'Merriweather', serif",
  "system-ui, sans-serif",
];

export const TypographyTab: React.FC<TypographyTabProps> = ({
  config,
  onSave,
  saving,
}) => {
  const { t } = useTranslation("common");

  const initialValues: IBrandingTypography =
    config?.typography || DEFAULT_BRANDING_TYPOGRAPHY;

  return (
    <Box p="16px">
      <Text fontWeight={600} fontSize="18px" mb="16px">
        {t("admin.branding.typography.title")}
      </Text>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => onSave(values)}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Flex flexDir="column" gap="24px">
              {/* Preview */}
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="12px"
                p="24px"
              >
                <Text
                  fontSize="24px"
                  fontWeight={700}
                  fontFamily={values.headingFontFamily || values.fontFamily}
                  mb="8px"
                >
                  Heading Preview
                </Text>
                <Text fontSize="16px" fontFamily={values.fontFamily}>
                  Body text preview. The quick brown fox jumps over the lazy dog.
                </Text>
              </Box>

              {/* Font Selection */}
              <Flex gap="24px" flexWrap="wrap">
                <FormControl maxW="400px">
                  <FormLabel fontSize="14px" fontWeight={500}>
                    {t("admin.branding.typography.fontFamily")}
                  </FormLabel>
                  <Select
                    value={values.fontFamily}
                    onChange={(e) =>
                      setFieldValue("fontFamily", e.target.value)
                    }
                    borderRadius="8px"
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font.split(",")[0].replace(/'/g, "")}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl maxW="400px">
                  <FormLabel fontSize="14px" fontWeight={500}>
                    {t("admin.branding.typography.headingFont")}
                  </FormLabel>
                  <Select
                    value={values.headingFontFamily || values.fontFamily}
                    onChange={(e) =>
                      setFieldValue("headingFontFamily", e.target.value)
                    }
                    borderRadius="8px"
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font.split(",")[0].replace(/'/g, "")}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Flex>

              <Flex justify="flex-end">
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={saving}
                  px="32px"
                >
                  {t("admin.branding.save")}
                </Button>
              </Flex>
            </Flex>
          </form>
        )}
      </Formik>
    </Box>
  );
};
